/**
 * Receipt formatter — ports PrinterManagerV2.kt formatting logic.
 *
 * Builds a ReceiptElement[] array from order data.
 * This is the single source of truth for receipt layout.
 * Platform-specific renderers (DantSu, React preview, future iOS)
 * consume this array.
 */
import type { DeepOrder, OrderItem } from '../types'
import type {
  ReceiptElement,
  LabelSettings,
  ReceiptRestaurantInfo,
  ReceiptMenuItemInfo,
} from './receiptTypes'

// ─── Labels ──────────────────────────────────────────────────
// Verbatim from the old RestaurantApp string resources (values, values-de, values-es).

const RECEIPT_LABELS = {
  de: {
    dineIn: 'Im Restaurant', delivery: 'Lieferung', takeaway: 'Abholung', desiredTime: 'Gewünschte Zeit',
    notPaid: 'Noch nicht bezahlt', paid: 'Bezahlt', checkPayment: 'Zahlung prüfen', paymentBy: 'Zahlung: {0}',
    cash: 'Bar', online: 'Online', cancelled: 'Bestellung storniert', cancellationReason: 'Stornierungsgrund',
    deliveryAddress: 'Lieferadresse', customerData: 'Kundendaten', table: 'Tisch {0}', comment: 'Kommentar: {0}',
    items: 'BESTELLUNG', extraDish: 'Extra Gericht:', subtotal: 'Summe:', discount: 'Rabatt:',
    maxDiscount: 'Maximaler Rabatt: {0}', deliveryFee: 'Liefer-Gebühr:', voucher: 'Gutschein:', tips: 'Trinkgeld:',
    total: 'Gesamtbetrag:', vat: 'MWSt {0}%:', net: 'Summe ohne MWSt:', date: 'Datum: {0}',
    scanForDelivery: 'Für die Lieferung scannen',
  },
  en: {
    dineIn: 'Dining in', delivery: 'Delivery', takeaway: 'Takeaway', desiredTime: 'Desired Time',
    notPaid: 'Not yet paid', paid: 'Paid', checkPayment: 'Check payment', paymentBy: 'Payment: {0}',
    cash: 'Cash', online: 'Online', cancelled: 'Order cancelled', cancellationReason: 'Cancellation reason',
    deliveryAddress: 'Delivery address', customerData: 'Customer data', table: 'Table {0}', comment: 'Comments: {0}',
    items: 'ORDER', extraDish: 'Extra dish:', subtotal: 'Subtotal:', discount: 'Discount:',
    maxDiscount: 'Max discount: {0}', deliveryFee: 'Delivery Fee:', voucher: 'Voucher discount:', tips: 'Tips:',
    total: 'Total:', vat: 'VAT {0}%:', net: 'Total without VAT:', date: 'Date: {0}',
    scanForDelivery: 'Scan for delivery',
  },
  es: {
    dineIn: 'En restaurante', delivery: 'Entrega', takeaway: 'Recogida', desiredTime: 'Hora elegida',
    notPaid: 'Todavía no pagado', paid: 'Pagado', checkPayment: 'Verifique Pago', paymentBy: 'Pago: {0}',
    cash: 'Efectivo', online: 'En línea', cancelled: 'Pedido cancelado', cancellationReason: 'Motivo de cancelación',
    deliveryAddress: 'Dirección de entrega', customerData: 'Datos del cliente', table: 'Mesa {0}', comment: 'Comentario: {0}',
    items: 'PEDIDO', extraDish: 'Plato adicional:', subtotal: 'Suma:', discount: 'Descuento:',
    maxDiscount: 'Descuento máximo: {0}', deliveryFee: 'Precio de entrega:', voucher: 'Vale:', tips: 'Propina:',
    total: 'Total:', vat: 'IVA {0}%:', net: 'Exl. IVA:', date: 'Fecha: {0}',
    scanForDelivery: 'Escanear para entregar',
  },
} as const

type ReceiptLanguage = keyof typeof RECEIPT_LABELS
type Labels = (typeof RECEIPT_LABELS)[ReceiptLanguage]

function fill(template: string, value: string | number): string {
  return template.replace('{0}', String(value))
}

// The old app formatted with the restaurant currency's locale, independent of the device language.
const CURRENCY_LOCALES: Record<string, string> = { EUR: 'de-DE', GBP: 'en-GB', USD: 'en-US' }

function formatMoney(amount: number, currency: string): string {
  const code = CURRENCY_LOCALES[currency] ? currency : 'EUR'
  return new Intl.NumberFormat(CURRENCY_LOCALES[code], { style: 'currency', currency: code }).format(amount)
}

const pad2 = (value: number) => String(value).padStart(2, '0')
const formatDay = (date: Date) => `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`
const formatClock = (date: Date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`

// ─── Public API ──────────────────────────────────────────────

export interface BuildReceiptOptions {
  order: DeepOrder
  restaurant: ReceiptRestaurantInfo
  labelSettings: LabelSettings
  menuItems: ReceiptMenuItemInfo[]
  logoHex?: string // Pre-converted logo image hex (from native imageToHex)
  logoSrc?: string // Logo image URL, used by the preview only
  resolvedAddress?: string // Geocoded delivery address
  /** Option group labels (variation category displayName) by category id; blank or missing → no label, as in the old app */
  variationCategoryLabels?: ReadonlyMap<number, string | null>
}

/**
 * Build the complete receipt as a ReceiptElement[] array.
 * Matches PrinterManagerV2.printOrder() output; deliberate differences only where the old app printed garbage
 * ("Tel. null", raw "%1$s" templates, "unknown" for Stripe) or crashed.
 */
export function buildReceipt(options: BuildReceiptOptions): ReceiptElement[] {
  const { order, restaurant, labelSettings, menuItems, logoHex, logoSrc, resolvedAddress } = options
  const maxChars = labelSettings.printerNbrCharactersPerLine
  const language: ReceiptLanguage = restaurant.language && restaurant.language in RECEIPT_LABELS
    ? restaurant.language as ReceiptLanguage : 'de'
  const labels = RECEIPT_LABELS[language]
  const upper = (text: string) => text.toLocaleUpperCase(language)
  const money = (amount: number) => formatMoney(amount, restaurant.currency)
  const isDelivery = order.withDelivery && !order.dinein

  const elements: ReceiptElement[] = []
  const isPos = order.userId.startsWith('pos:')
  if (isPos) {
    elements.push({ type: 'text', align: 'center', text: 'Küchenbon', bold: true })
    elements.push({ type: 'text', align: 'center', text: 'Kein Kassenbeleg' })
  }

  // ── Header chunk ──
  if (labelSettings.printLogo && (logoHex || logoSrc)) {
    elements.push({ type: 'line', char: '_' }, { type: 'image', hex: logoHex ?? '', src: logoSrc })
  }
  appendCentered(elements, restaurant.name, maxChars, true)
  appendCentered(elements, `${restaurant.streetName} ${restaurant.houseNumber}`, maxChars)
  appendCentered(elements, `${restaurant.postcode} ${restaurant.city}`, maxChars)
  if (restaurant.phoneNumber) appendCentered(elements, `Tel. ${restaurant.phoneNumber}`, maxChars)
  elements.push({ type: 'feed' }, { type: 'line', char: '_' })
  appendCentered(elements, upper(order.dinein ? labels.dineIn : order.withDelivery ? labels.delivery : labels.takeaway), maxChars, true)
  appendCentered(elements, `# ${order.id}`, maxChars, true)
  if (order.commitedTime) {
    const date = new Date(order.commitedTime)
    elements.push({ type: 'feed' })
    appendCentered(elements, labels.desiredTime, maxChars)
    appendCentered(elements, formatClock(date), maxChars, true)
    appendCentered(elements, formatDay(date), maxChars)
  }
  elements.push({ type: 'feed' })
  appendCentered(elements, upper(getPaymentStatusText(order, labels)), maxChars, true)
  appendCentered(elements, getPaymentTypeText(order, labels), maxChars)
  if (order.cancelStatus === 'cancelled') {
    elements.push({ type: 'feed' })
    appendCentered(elements, upper(labels.cancelled), maxChars, true)
    if (order.cancelReason?.trim()) {
      appendReceiptRow(elements, `${labels.cancellationReason}: ${order.cancelReason}`, '', null, maxChars, true)
    }
  }
  elements.push({ type: 'line', char: '_' }, { type: 'feed' })
  appendReceiptRow(elements, upper(isDelivery ? labels.deliveryAddress : labels.customerData), '', null, maxChars, true)
  if (order.dinein && order.tableNumber) {
    appendReceiptRow(elements, fill(labels.table, order.tableNumber), '', null, maxChars, true)
  }
  if (order.deliveryAddress?.name.trim()) appendReceiptRow(elements, order.deliveryAddress.name, '', null, maxChars, true)
  if (isDelivery && order.deliveryAddress) {
    const addressLines = (resolvedAddress ?? order.deliveryAddress.city).split(', ')
    for (const line of addressLines) appendReceiptRow(elements, line, '', null, maxChars)
  }
  if (order.telephone.trim()) appendReceiptRow(elements, `Tel. ${order.telephone}`, '', null, maxChars)
  if (order.deliveryAddress?.comments?.trim()) {
    elements.push({ type: 'feed' })
    appendReceiptRow(elements, fill(labels.comment, order.deliveryAddress.comments), '', null, maxChars, true)
  }
  elements.push({ type: 'feed' }, { type: 'line', char: '_' }, { type: 'chunkBreak' })

  // ── Items chunk ──
  appendReceiptRow(elements, labels.items, '', null, maxChars, true)

  const itemsByCategory = groupItemsByCategory(order.orderItems, menuItems)

  for (const [categoryName, items] of itemsByCategory) {
    // Category header
    elements.push({ type: 'text', align: 'left', text: categoryName, bold: true })

    for (const item of items) {
      const menuItem = menuItems.find(m => m.id === item.menuItemId)
      buildOrderItemElements(elements, item, menuItem, labelSettings, maxChars, money, options.variationCategoryLabels)
    }
  }

  // Extra items
  if (order.extraItems.length > 0) {
    elements.push({ type: 'feed' })
    elements.push({ type: 'text', align: 'left', text: labels.extraDish })

    for (const extra of order.extraItems) {
      if (extra.price !== 0) {
        elements.push({
          type: 'columns',
          left: `1x ${extra.name}`,
          right: money(extra.price),
          rightBold: true,
        })
      } else {
        elements.push({ type: 'text', align: 'left', text: `1x ${extra.name}` })
      }
    }
  }

  elements.push({ type: 'feed' }, { type: 'line', char: '_' }, { type: 'chunkBreak' })

  // ── Footer chunk ──

  // Subtotal
  const subtotal = calculateCartItemsDiscount(order)
  elements.push({
    type: 'columns',
    left: labels.subtotal,
    right: money(subtotal),
    rightBold: true,
  })

  // Discount
  const discountValue = subtotal - order.discountPrice
  if (discountValue > 0.005) {
    if (order.fullDiscountPrice === order.discountPrice) {
      elements.push({
        type: 'columns',
        left: labels.discount,
        right: money(discountValue),
      })
    } else {
      elements.push({
        type: 'columns',
        left: fill(labels.maxDiscount, money(order.maximumPrice)),
        right: money(discountValue),
      })
    }
  }

  // Delivery fee
  if (isDelivery) {
    elements.push({
      type: 'columns',
      left: labels.deliveryFee,
      right: money(order.deliveryFee),
    })
  }

  // Voucher
  const voucherDiscount = getVoucherDiscount(order)
  if (voucherDiscount > 0) {
    elements.push({
      type: 'columns',
      left: labels.voucher,
      right: money(voucherDiscount),
      leftBold: true,
      rightBold: true,
    })
  }

  // Tips and adjustments precede the emphasized final amount.
  if (order.tipsFee > 0) {
    elements.push({ type: 'columns', left: labels.tips, right: money(order.tipsFee) })
  }
  const total = order.discountPrice - voucherDiscount + order.deliveryFee + order.tipsFee
  elements.push({ type: 'line', char: '_' })
  appendReceiptRow(elements, labels.total, '', money(total), maxChars, true)
  elements.push({ type: 'feed' })
  // A POS kitchen ticket must not present live-menu VAT as a frozen fiscal breakdown.
  if (!isPos) buildTaxRateElements(elements, order, restaurant, menuItems, labels, money)
  elements.push({ type: 'feed' })
  if (order.date) {
    const date = new Date(order.date)
    appendReceiptRow(elements, fill(labels.date, `${formatDay(date)} ${formatClock(date)}`), '', null, maxChars)
  }
  elements.push({ type: 'line', char: '_' })

  // QR code (for delivery orders) — the firmware QR (default) links the address, the DantSu image QR adds the place id.
  const printQr = labelSettings.printDeliveryQr && isDelivery && !!resolvedAddress
  if (printQr && resolvedAddress) {
    elements.push({ type: 'feed' })
    appendCentered(elements, labels.scanForDelivery, maxChars)
    const encodedAddress = resolvedAddress.replace(/ /g, '+')
    if (labelSettings.useNativeQrCode) {
      elements.push({ type: 'qrcode', text: `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, native: true })
    } else {
      const placeId = order.deliveryAddress?.city ?? ''
      const qrUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}&query_place_id=${placeId}`
      elements.push({ type: 'qrcode', text: qrUrl, size: 25 })
    }
  }

  // Tear-off margin: the date was torn off on short-feed printers. Two blank lines, then two rules, which always
  // print (trailing blank lines get dropped). The firmware QR is printed after the text and feeds on its own.
  if (!(printQr && labelSettings.useNativeQrCode)) {
    elements.push({ type: 'feed', lines: 2 }, { type: 'line', char: '_' }, { type: 'line', char: '_' })
  }

  elements.push({ type: 'feed', lines: 3 })

  return elements
}

// ─── Item formatting ─────────────────────────────────────────

function buildOrderItemElements(
  elements: ReceiptElement[],
  item: OrderItem,
  menuItem: ReceiptMenuItemInfo | undefined,
  labelSettings: LabelSettings,
  maxChars: number,
  money: (amount: number) => string,
  variationCategoryLabels: ReadonlyMap<number, string | null> | undefined,
): void {
  const quantity = item.quantity ?? 1
  const totalPrice = item.discountPrice * quantity
  const priceText = money(totalPrice)
  const quantityPrefix = `${quantity}x `

  appendReceiptRow(elements, quantityPrefix + item.name, '', priceText, maxChars, true)

  if (labelSettings.printMenuItemDescription && menuItem?.description) {
    appendReceiptRow(elements, menuItem.description, '  ', null, maxChars)
  }

  for (const [categoryId, variations] of getVariationCategories(item)) {
    // The old app prints the group's displayName only; the internal name carries an ordering prefix.
    const label = variationCategoryLabels?.get(categoryId)?.trim()
    if (label) appendReceiptRow(elements, label, '  ', null, maxChars, true)
    for (const variation of variations) {
      const price = variation.takeawayPrice
      const prefix = price > 0 ? '+ ' : ''
      appendReceiptRow(elements, prefix + (variation.name ?? ''), '    ',
        price === 0 ? null : money(price), maxChars)
    }
  }
}

// ─── Tax rate calculation ────────────────────────────────────
// Matches PrinterManagerV2.getTaxRatesText() exactly

function buildTaxRateElements(
  elements: ReceiptElement[],
  order: DeepOrder,
  restaurant: ReceiptRestaurantInfo,
  menuItems: ReceiptMenuItemInfo[],
  labels: Labels,
  money: (amount: number) => string,
): void {
  const discount = calculateCartItemsDiscount(order) - order.discountPrice
  const cartTotal = calculateCartItemsDiscount(order)
  const isDelivery = order.withDelivery && !order.dinein

  const taxRateSums = new Map<number, number>()

  for (const item of order.orderItems) {
    const menuItem = menuItems.find(m => m.id === item.menuItemId)
    let taxRate = menuItem?.taxRate ?? 0
    if (taxRate === 0) taxRate = restaurant.defaultTaxRate

    if (taxRate !== 0) {
      const quantity = item.quantity ?? 1
      let priceWithQuantity = item.discountPrice * quantity

      // Subtract proportional discount
      if (cartTotal > 0) {
        priceWithQuantity -= discount * (priceWithQuantity / cartTotal)
      }

      // Options with their own rate (e.g. a 19% drink in a 7% deal) take their price share of the line,
      // as in the backend tax report (TaxRateRepository.splitVariationTaxRates).
      const shares = new Map<number, number>()
      for (const variation of item.variations) {
        if (variation.taxRate !== undefined && variation.taxRate !== taxRate && variation.takeawayPrice > 0)
          shares.set(variation.taxRate, (shares.get(variation.taxRate) ?? 0) + variation.takeawayPrice)
      }
      const sharedTotal = [...shares.values()].reduce((sum, price) => sum + price, 0)
      const parts: [number, number][] = shares.size > 0 && item.price > 0 && sharedTotal <= item.price
        ? [...shares].map(([rate, price]) => [rate, priceWithQuantity * price / item.price])
        : []
      parts.unshift([taxRate, priceWithQuantity - parts.reduce((sum, [, gross]) => sum + gross, 0)])

      for (const [rate, gross] of parts) {
        const taxValue = gross - gross / (rate / 100 + 1)
        taxRateSums.set(rate, (taxRateSums.get(rate) ?? 0) + taxValue)
      }
    }
  }

  // Delivery fee tax
  if (isDelivery && restaurant.defaultTaxRate !== 0) {
    const taxRate = restaurant.defaultTaxRate
    const taxFactor = taxRate / 100
    const totalWithoutTax = order.deliveryFee / (taxFactor + 1)
    const taxValue = order.deliveryFee - totalWithoutTax

    const existing = taxRateSums.get(taxRate) ?? 0
    taxRateSums.set(taxRate, existing + taxValue)
  }

  if (taxRateSums.size > 0) {
    // Ascending rate (7% before 19%), as the old app printed.
    for (const [rate, value] of [...taxRateSums].sort(([a], [b]) => a - b)) {
      elements.push({
        type: 'columns',
        left: fill(labels.vat, rate),
        right: money(round2(value)),
      })
    }

    // Total without tax
    const totalWithDeliveryAndTips = order.discountPrice + order.deliveryFee + order.tipsFee
    let totalNetto = totalWithDeliveryAndTips
    for (const tax of taxRateSums.values()) {
      totalNetto -= tax
    }

    elements.push({
      type: 'columns',
      left: labels.net,
      right: money(round2(totalNetto)),
    })
  }
}

// ─── Helpers ─────────────────────────────────────────────────

function getPaymentStatusText(order: DeepOrder, labels: Labels): string {
  if (order.userId.startsWith('pos:')) {
    if (order.posPayment) return 'Bezahlt'
    if (order.posPaymentUnavailable || order.posPayment === undefined) return 'Zahlung prüfen'
    return 'Noch nicht bezahlt'
  }
  switch (order.paymentStatus.toLowerCase()) {
    case 'cash': return labels.notPaid
    case 'paypal_success':
    case 'wirecard_success':
    case 'cc_success':
    case 'stripe_success': return labels.paid
    case 'paypalme_new': return labels.checkPayment
    default: return order.paymentStatus
  }
}

function getPaymentTypeText(order: DeepOrder, labels: Labels): string {
  if (order.userId.startsWith('pos:')) return order.posPayment
    ? fill(labels.paymentBy, order.posPayment.method === 'cash' ? labels.cash : 'Karte') : 'Zahlungsart: offen'
  switch (order.paymentStatus.toLowerCase()) {
    case 'cash': return fill(labels.paymentBy, labels.cash)
    case 'paypal_success': return fill(labels.paymentBy, 'PayPal')
    case 'paypalme_new': return fill(labels.paymentBy, 'PayPal.me')
    case 'wirecard_success':
    case 'cc_success':
    case 'stripe_success': return fill(labels.paymentBy, labels.online)
    default: return fill(labels.paymentBy, order.paymentStatus)
  }
}

/** Sum of discountPrice * quantity for all items — matches Order.getCartItemsDiscount() */
function calculateCartItemsDiscount(order: DeepOrder): number {
  return order.orderItems.reduce((sum, item) => {
    return sum + item.discountPrice * (item.quantity ?? 1)
  }, 0)
}

function getVoucherDiscount(order: DeepOrder): number {
  if (!order.vouchersUsed?.length) return 0
  return order.vouchersUsed[0].discount
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * Group order items by menu category.
 * Returns Map<categoryName, OrderItem[]> ordered by menu position.
 * Matches PrinterManagerV2.builderOrderItemsText() grouping.
 */
function groupItemsByCategory(
  orderItems: OrderItem[],
  menuItems: ReceiptMenuItemInfo[],
): Map<string, OrderItem[]> {
  const menuItemMap = new Map(menuItems.map(m => [m.id, m]))
  const categoryMap = new Map<string, { items: OrderItem[]; position: number }>()

  for (const item of orderItems) {
    const menuItem = menuItemMap.get(item.menuItemId)
    const categoryName = menuItem?.categoryName ?? ''
    const position = menuItem?.menuposition ?? 999

    if (!categoryMap.has(categoryName)) {
      categoryMap.set(categoryName, { items: [], position })
    }
    categoryMap.get(categoryName)!.items.push(item)
  }

  // Sort by menu position (ascending)
  const sorted = [...categoryMap.entries()].sort((a, b) => a[1].position - b[1].position)
  return new Map(sorted.map(([name, data]) => [name, data.items]))
}

/**
 * Get variations grouped by category id for an order item, in menu order
 * (category name carries the configured order prefix, as in the customer app; then id).
 */
function getVariationCategories(
  item: OrderItem,
): Map<number, typeof item.variations> {
  const categories = new Map<number, typeof item.variations>()

  const sorted = [...item.variations].sort((a, b) => {
    const nameA = a.menuItemVariationCategoryName ?? ''
    const nameB = b.menuItemVariationCategoryName ?? ''
    const byMenuOrder = nameA < nameB ? -1 : nameA > nameB ? 1 : 0
    return byMenuOrder || a.menuItemVariationCategoryId - b.menuItemVariationCategoryId
  })

  for (const variation of sorted) {
    const categoryId = variation.menuItemVariationCategoryId
    if (!categories.has(categoryId)) {
      categories.set(categoryId, [])
    }
    categories.get(categoryId)!.push(variation)
  }

  return categories
}

/** Keep text at full width; put a nonzero price below only when it cannot fit. */
function appendReceiptRow(
  elements: ReceiptElement[], text: string, indent: string, price: string | null,
  maxChars: number, bold = false,
): void {
  const lines = wrapReceiptText(text, maxChars - indent.length)
  const priceFits = price !== null && indent.length + lines[lines.length - 1].length + price.length < maxChars
  lines.forEach((line, index) => {
    if (index === lines.length - 1 && priceFits) {
      elements.push({ type: 'columns', left: indent + line, right: price, leftBold: bold, rightBold: bold })
    } else {
      elements.push({ type: 'text', align: 'left', text: indent + line, bold })
    }
  })
  if (price !== null && !priceFits) elements.push({ type: 'text', align: 'right', text: price, bold })
}

function wrapReceiptText(text: string, width: number): string[] {
  const limit = Math.max(1, width)
  return text.replace(/<br\s*\/?>/gi, '\n').split('\n').flatMap(paragraph => {
    const lines: string[] = []
    let remaining = paragraph.trim().replace(/[\s\u00a0]+/g, ' ')
    while (remaining.length > limit) {
      const space = remaining.lastIndexOf(' ', limit)
      const hyphen = remaining.lastIndexOf('-', limit - 1)
      const end = space > 0 ? space : hyphen >= 0 ? hyphen + 1 : limit
      lines.push(remaining.substring(0, end).trimEnd())
      remaining = remaining.substring(end).trimStart()
    }
    lines.push(remaining)
    return lines
  })
}

/** Center and wrap at the standard printer text size for consistent printer support. */
function appendCentered(elements: ReceiptElement[], text: string, maxChars: number, bold = false): void {
  for (const line of wrapReceiptText(text, maxChars)) {
    if (line) elements.push({ type: 'text', align: 'center', text: line, bold })
  }
}
